import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Typography,
  useTheme,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import Logo from "./Logo";
import { useNavigate } from "react-router-dom";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { brandFilledButtonSx } from "../utils/brandAssets";

const MarketingNav = () => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const handleLoginClick = () => {
    navigate("/login");
  };

  const handleSignupClick = () => {
    navigate("/signup");
  };

  const handleMobileDrawerOpen = () => {
    setMobileDrawerOpen(true);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleFeaturesClickDrawer = () => {
    const featuresSection = document.getElementById("features-section");
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: "smooth" });
    }
    handleMobileDrawerClose();
  };

  const handleAboutClickDrawer = () => {
    navigate("/about");
    handleMobileDrawerClose();
  };

  const handleSignupClickDrawer = () => {
    handleMobileDrawerClose();
    navigate("/signup");
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backdropFilter: "blur(12px)",
          bgcolor: "rgba(20, 20, 20, 0.85)",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ px: { xs: 0 }, py: 0, minHeight: { xs: 64, sm: 64 } }}>
            {/* Mobile Layout */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                width: "100%",
                alignItems: "center",
              }}
            >
              {/* Hamburger Menu */}
              <IconButton
                color="inherit"
                onClick={handleMobileDrawerOpen}
                sx={{ color: "text.primary" }}
              >
                <MenuIcon />
              </IconButton>

              {/* Centered Logo */}
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <Logo />
              </Box>

              {/* Sign In Button */}
              {!isAuthenticated && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleLoginClick}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor: "divider",
                    "&:hover": {
                      borderColor: "text.secondary",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  Sign in
                </Button>
              )}
            </Box>

            {/* Desktop Layout */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                flexGrow: 1,
              }}
            >
              <Logo />
            </Box>

            {/* Navigation Links - Desktop */}
            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3, mr: 3 }}>
              <Button
                color="inherit"
                onClick={() => {
                  const featuresSection =
                    document.getElementById("features-section");
                  if (featuresSection) {
                    featuresSection.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{ textTransform: "none", fontWeight: 500 }}
              >
                Features
              </Button>
              <Button
                color="inherit"
                onClick={() => navigate("/about")}
                sx={{ textTransform: "none", fontWeight: 500 }}
              >
                About
              </Button>
            </Box>

            {/* Auth Buttons - Desktop Only */}
            {!isAuthenticated ? (
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Button
                  variant="text"
                  color="inherit"
                  onClick={handleLoginClick}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Sign in
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSignupClick}
                  sx={{
                    ...brandFilledButtonSx,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    ml: 3,
                  }}
                >
                  Sign up
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={() => (window.location.href = "/app")}
                sx={{
                  ...brandFilledButtonSx,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  display: { xs: "none", md: "block" },
                }}
              >
                Go to App
              </Button>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Spacer for fixed AppBar */}
      <Toolbar />

      {/* Mobile Full-Screen Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerClose}
        sx={{
          display: { xs: "block", md: "none" },
          zIndex: theme.zIndex.drawer - 1,
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "calc(100% - 64px)",
            bgcolor: "background.paper",
            zIndex: theme.zIndex.drawer - 1,
            top: "64px",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Menu
              </Typography>
              <IconButton
                onClick={handleMobileDrawerClose}
                aria-label="Close menu"
                sx={{
                  color: "text.primary",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Navigation Items */}
          <List sx={{ flexGrow: 1, pt: 2 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleFeaturesClickDrawer}
                sx={{ py: 2 }}
              >
                <ListItemText primary="Features" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleAboutClickDrawer} sx={{ py: 2 }}>
                <ListItemText primary="About" />
              </ListItemButton>
            </ListItem>
          </List>

          {/* Sign Up Button at Bottom */}
          {!isAuthenticated && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSignupClickDrawer}
                sx={{
                  ...brandFilledButtonSx,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                }}
              >
                Sign up
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default MarketingNav;
